import Drawer from '@material-ui/core/Drawer';
import {toast} from '@wandb/weave/common/components/elements/Toast';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {useRefreshAllNodes} from '@wandb/weave/react';
import React, {useEffect, useState} from 'react';

import {useUpdateArtifactMutation} from '../../../../../generated/graphql';
import {
  differenceInDaysExact,
  NUM_SECONDS_IN_DAY,
} from '../../../../../util/date';
import {
  getErrMsgFromError,
  propagateErrorsContext,
} from '../../../../../util/errors';
import {
  ArtifactTTL,
  isInvalidTTLString,
  ttlInputErrorMsg,
} from '../../../../ArtifactTTL/utils';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../../../../Banner';
import {ResizableDrawer} from '../../ResizableDrawer';
import {Artifact} from '../CreateRegisteredCollectionDrawerInner';
import {EditArtifactTTLSummary} from './EditArtifactTTLSummary';

interface OptionType {
  label: string;
  value: any;
}

type EditTTLDrawerProps = {
  open: boolean;
  closeDrawer: () => void;
  artifact: Artifact;
  ttlIsInherited: boolean;
  entityTTL: number;
};

export const EditTTLDrawer = ({
  open,
  closeDrawer,
  artifact,
  ttlIsInherited,
  entityTTL,
}: EditTTLDrawerProps) => {
  const refreshNodes = useRefreshAllNodes();
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };

  const policyDropdown = {
    version: 'Custom',
    team: "Team's default",
    disable: 'Deactivate',
  };

  // team TTL
  const entityTTLDays =
    entityTTL > 0 ? entityTTL / NUM_SECONDS_IN_DAY : undefined;
  const hasEntityTTL = entityTTL > 0;

  const policyDropdownOptions: OptionType[] = [
    {label: policyDropdown.version, value: 'version'},
    {label: policyDropdown.disable, value: 'disable'},
  ];
  if (hasEntityTTL) {
    policyDropdownOptions.push({
      label: policyDropdown.team,
      value: 'team',
    });
  }

  // version TTL
  const versionTTLDays =
    artifact.ttlDurationSeconds && artifact.ttlDurationSeconds > 0
      ? artifact.ttlDurationSeconds / NUM_SECONDS_IN_DAY
      : undefined;

  // Team: If we are inheriting the TTL and there is a team TTL
  // Disable: TTL is set to disable, we do not inherit for team TTL
  // Version: There is a custom version TTL or the user hasn't set any team or version TTLs yet
  const [policyMode, setPolicyMode] = useState<'version' | 'team' | 'disable'>(
    ttlIsInherited && entityTTL > 0
      ? 'team'
      : !ttlIsInherited && versionTTLDays == null
        ? 'disable'
        : 'version'
  );

  const [ttlDurationInput, setTTLDurationInput] = useState(
    ttlIsInherited ? entityTTLDays : versionTTLDays
  );

  const [ttlDurationInputString, setTTLDurationInputString] = useState(
    ttlDurationInput ? String(Math.round(ttlDurationInput)) : ''
  );

  // Used to save original TTL to see if user changed settings
  const [originalTTLDuration] = useState(ttlDurationInput);
  const [originalPolicy] = useState(policyMode);

  const [disableButton, setDisableButton] = useState(true);

  const now = new Date();
  const [artifactAgeDays] = useState(
    artifact.createdAt
      ? differenceInDaysExact(now, new Date(artifact.createdAt))
      : undefined
  );
  const [ttlRemainingDays, setTTLRemainingDays] = useState(
    ttlDurationInput && artifactAgeDays
      ? ttlDurationInput - artifactAgeDays
      : undefined
  );

  const [updateArtifact] = useUpdateArtifactMutation({
    context: propagateErrorsContext(),
  });

  useEffect(() => {
    setTTLRemainingDays(
      ttlDurationInput && artifactAgeDays
        ? ttlDurationInput - artifactAgeDays
        : undefined
    );
  }, [ttlDurationInput, artifactAgeDays]);

  useEffect(() => {
    setTTLDurationInputString(
      policyMode === 'team'
        ? String(entityTTLDays)
        : versionTTLDays
          ? String(Math.round(versionTTLDays))
          : ''
    );
    setTTLDurationInput(policyMode === 'team' ? entityTTLDays : versionTTLDays);
  }, [policyMode, entityTTLDays, versionTTLDays]);

  useEffect(() => {
    // Disable if input invalid or settings haven't changed
    setDisableButton(
      isInvalidTTLString(ttlDurationInputString) ||
        (ttlDurationInput === originalTTLDuration &&
          policyMode === originalPolicy)
    );
  }, [
    ttlDurationInput,
    ttlDurationInputString,
    policyMode,
    originalTTLDuration,
    originalPolicy,
  ]);

  return (
    <Drawer anchor="right" open={open} onClose={closeDrawer}>
      <ResizableDrawer
        startWidth={width}
        minWidth={600}
        onSetWidth={onSetWidth}>
        <Tailwind style={{width: '100%', height: '100%'}}>
          <div className="h-full w-full px-24 pb-20 pt-60">
            <div className="flex h-full w-full flex-col">
              <div className="flex items-center">
                <div className="py-20 text-xl font-semibold leading-8">
                  Edit TTL policy for "{artifact.sequenceName}:v
                  {artifact.versionIndex}"
                </div>
                <div className="flex-1 text-right">
                  <Button
                    onClick={closeDrawer}
                    icon="close"
                    size="large"
                    variant="ghost"
                  />
                </div>
              </div>

              <div className="-mx-20 h-1 bg-moon-250"></div>

              <div className="pb-300 w-full flex-1 overflow-y-auto p-4">
                <div className="mb-16 mt-20">
                  <div>
                    <div className="mb-4 mt-24 flex items-center">
                      <div className="text-[16px] font-semibold leading-8">
                        TTL policy
                      </div>
                    </div>
                    <div className="w-[180px]">
                      <Select<OptionType>
                        options={policyDropdownOptions}
                        defaultValue={{
                          label: policyDropdown[policyMode],
                          value: policyMode,
                        }}
                        onChange={selected => {
                          if (selected != null && selected.value) {
                            console.log(selected.value);
                            setPolicyMode(selected.value);
                          }
                        }}
                      />
                    </div>
                  </div>

                  {policyMode !== 'disable' && (
                    <div>
                      <div className="mb-4 mt-24 flex items-center">
                        <div className="text-[16px] font-semibold leading-8">
                          TTL duration
                        </div>
                      </div>
                      <div className="h-150px w-full">
                        <div className="flex">
                          <div className="w-[70px]">
                            <TextField
                              value={ttlDurationInputString ?? ''}
                              onChange={value => {
                                const isNumber = /^\d+$/.test(value);
                                if (!value || isNumber) {
                                  setTTLDurationInputString(value);
                                  setTTLDurationInput(
                                    isInvalidTTLString(value)
                                      ? undefined
                                      : Number(value)
                                  );
                                }
                              }}
                              disabled={policyMode === 'team'}
                              errorState={isInvalidTTLString(
                                ttlDurationInputString
                              )}
                            />
                          </div>
                          <div className="flex items-center px-8">days</div>
                        </div>
                        {ttlDurationInputString !== '' &&
                          isInvalidTTLString(ttlDurationInputString) && (
                            <div className="py-8 text-sm text-red-550">
                              {ttlInputErrorMsg}
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                  {policyMode !== 'disable' &&
                    artifactAgeDays != null &&
                    ttlRemainingDays != null && (
                      <div>
                        <div className="mb-4 mt-24 flex items-center">
                          <div className="text-[16px] font-semibold leading-8">
                            Summary of settings
                          </div>
                        </div>

                        <EditArtifactTTLSummary
                          artifactAgeDays={artifactAgeDays}
                          ttlRemainingDays={ttlRemainingDays}
                        />
                      </div>
                    )}

                  {policyMode === 'disable' && (
                    <Banner variant="info" className="mt-24">
                      <BannerFlexWrapper>
                        <Icon name="info" style={{flex: 'none'}} />
                        <BannerTextWrapper>
                          <div>
                            The artifact{' '}
                            {artifact.sequenceName +
                              ':v' +
                              artifact.versionIndex}{' '}
                            will be excluded from the teams default TTL policy
                            and will not be subject to automatic deletion based
                            on TTL rules.
                          </div>
                        </BannerTextWrapper>
                      </BannerFlexWrapper>
                    </Banner>
                  )}
                </div>
              </div>

              <div className="-mx-20 h-1 bg-moon-250"></div>
              <div className="flex items-center pt-20">
                <Button
                  className="w-full"
                  variant={
                    ttlRemainingDays &&
                    ttlRemainingDays <= 0 &&
                    policyMode !== 'disable'
                      ? 'destructive'
                      : 'primary'
                  }
                  size="large"
                  disabled={disableButton}
                  onClick={async () => {
                    let ttlDurationSeconds = ArtifactTTL.DISABLED;
                    if (policyMode === 'team') {
                      ttlDurationSeconds = ArtifactTTL.INHERIT;
                    } else if (policyMode === 'version') {
                      ttlDurationSeconds =
                        (ttlDurationInput ?? 0) * NUM_SECONDS_IN_DAY;
                    } else {
                      ttlDurationSeconds = ArtifactTTL.DISABLED;
                    }
                    let updateArtifactSuccess = true;
                    updateArtifact({
                      variables: {
                        artifactID: artifact.id,
                        ttlDurationSeconds,
                      },
                    }).catch(err => {
                      console.error(err);
                      const errMsg = getErrMsgFromError(err);
                      toast(`Error updating artifact TTL. error: ${errMsg}`, {
                        type: 'error',
                      });
                      updateArtifactSuccess = false;
                    });
                    await refreshNodes();
                    closeDrawer();
                    if (updateArtifactSuccess) {
                      toast(
                        'TTL ' +
                          (policyMode === 'disable'
                            ? 'deactivated '
                            : ' updated with ' + ttlDurationInput + ' days ') +
                          (policyMode === 'team'
                            ? "(Team's default)"
                            : '(Custom)') +
                          ' on the artifact ' +
                          artifact.sequenceName +
                          ':v' +
                          artifact.versionIndex
                      );
                    }
                  }}>
                  {ttlRemainingDays &&
                  ttlRemainingDays <= 0 &&
                  policyMode !== 'disable'
                    ? 'Delete artifact'
                    : 'Update TTL'}
                </Button>
              </div>
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};
